import {
  IField,
  IFormDocument,
  IListFilterBinder,
  IListFilterOption,
  ISelectOption,
  SelectType,
} from 'app/services/api5-service/api.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ListFilterControlWithValue,
  SelectControlWithValue,
  getTypedField,
} from 'common/dynamic-form/utils/form-preparing';
import { appendConfigCommonOptions } from 'common/dynamic-form/utils/formly-configs-generation';
import { getLayoutPlace } from 'common/dynamic-form/utils/get-layout-place';
import { DocHelper } from 'utils/dochelper';

import { IFilterTag } from './model/filter-tag.interface';
import { ListFilterProps } from './model/list-filter-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
} from '../../model';
import {
  CheckboxCaptions,
  getCheckboxConfig,
} from '../checkbox/checkbox.utils';
import { SelectConfig } from '../select/model';
import { getSelectConfig } from '../select/select.utils';
import { SwitcherConfig } from '../switcher/model';
import { getSwitcherConfig } from '../switcher/switcher.utils';

/**
 * Get form control name for filter list tags controls
 *
 * @WARN orion logic!
 * @param targetProperty
 * @param key - tag key
 */
function getFormControlNameForFilterControl(
  targetProperty: string,
  key: string,
): string {
  return `${targetProperty}__${key}`;
}

/**
 * Get tag name from control name
 *
 * @param name
 * @param targetProperty
 */
export function getTagKeyFromControlName(
  name: string,
  targetProperty: string,
): string {
  const prefix = `${targetProperty}__`;
  return name.replace(prefix, '');
}

/**
 * Get list filter options for list filter control
 *
 * @param control
 * @param doc
 */
function getListFilterOptions(
  control: IListFilterBinder,
  doc: IFormDocument,
): IListFilterOption[] {
  return DocHelper.getArrayWrapperValue(
    DocHelper.getArrayWrapperValue(doc.flist).find(
      meta => meta.$name === control.$name,
    )?.val,
  );
}

/**
 * Map filter options to filter tag
 *
 * @param option
 */
function getListFilterTag(option: IListFilterOption): IFilterTag {
  return {
    key: option.$key,
    msg: option.$,
    icon: option.$icon,
  };
}

/**
 * Map filter options to filter tag
 *
 * @param control
 * @param doc
 */
function getListFilterTags(
  control: IListFilterBinder,
  doc: IFormDocument,
): IFilterTag[] {
  return getListFilterOptions(control, doc).map(option =>
    getListFilterTag(option),
  );
}

/**
 * Get stream of list filter tags
 *
 * @param control
 * @param state
 */
function getListFilterTags$(
  control: IListFilterBinder,
  state: ISPFormState,
): Observable<IFilterTag[]> {
  return state.docSubject.pipe(map(doc => getListFilterTags(control, doc)));
}

/**
 * Map list filter tag to select option
 *
 * @param tag
 */
function getSelectOptionsFromTag(tag: IFilterTag): ISelectOption {
  return {
    $: tag.msg,
    $key: tag.key,
    $sprite: tag.icon,
    $spritesvg: tag.icon ? 'yes' : 'no',
  };
}

/**
 * Get select options from list filter options
 *
 * @param control
 * @param doc
 */
function getSelectOptionsFromFilter(
  control: IListFilterBinder,
  doc: IFormDocument,
): ISelectOption[] {
  const tags = getListFilterTags(control, doc);
  return tags.map(getSelectOptionsFromTag);
}

/**
 * Get select config with appended common options
 *
 * @param props
 * @param typedControl
 * @param field
 * @param state
 */
function getSelectConfigWithOptions(
  props: ListFilterProps,
  typedControl: ListFilterControlWithValue,
  field: IField,
  state: ISPFormState,
): SelectConfig {
  const control = typedControl.control;

  const selectTypedField = getTypedField(
    {
      ...field,
      select: [
        {
          $name: control.$name,
          $custom_label: 'yes',
          $type:
            control.$type === 'multiple'
              ? SelectType.ChipMultiple
              : SelectType.Chip,
        },
      ],
    },
    state.docSubject.value,
  );

  const selectTypedControl = selectTypedField.controls.find(
    otherTypedControl => otherTypedControl.type === 'select',
  ) as SelectControlWithValue;

  const selectConfig = getSelectConfig(selectTypedControl, state, {
    get: () =>
      getSelectOptionsFromFilter(typedControl.control, state.docSubject.value),
    stream$: props.tags$.pipe(map(tags => tags.map(getSelectOptionsFromTag))),
  });

  appendConfigCommonOptions(
    selectConfig,
    control,
    field,
    state,
    typedControl.value,
  );
  return selectConfig;
}

/**
 * Get switch config with appended common options
 *
 * @param props
 * @param typedControl
 * @param field
 * @param state
 */
function getSwitcherConfigWithOptions(
  props: ListFilterProps,
  typedControl: ListFilterControlWithValue,
  field: IField,
  state: ISPFormState,
): SwitcherConfig {
  const control = typedControl.control;

  const switcherConfig = getSwitcherConfig(
    {
      $name: control.$name,
    },
    field,
    state,
    {
      get: () =>
        getSelectOptionsFromFilter(
          typedControl.control,
          state.docSubject.value,
        ),
      stream$: props.tags$.pipe(map(tags => tags.map(getSelectOptionsFromTag))),
    },
  );

  appendConfigCommonOptions(
    switcherConfig,
    control,
    field,
    state,
    typedControl.value,
  );
  return switcherConfig;
}

/**
 * Get checkbox configs for filter type controls in model
 *
 * @param tags - tags options
 * @param targetProperty
 * @param field
 * @param state
 */
function getCheckboxConfigs(
  tags: IFilterTag[],
  targetProperty: string,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Checkbox>[] {
  return tags.map(tag => {
    const checkboxName = getFormControlNameForFilterControl(
      targetProperty,
      tag.key,
    );
    const checkboxConfig = getCheckboxConfig(
      {
        $name: checkboxName,
        $type: 'checkbox',
      },
      field,
      state,
    );

    const captions: CheckboxCaptions = {
      captionOff: tag.msg,
      captionOn: tag.msg,
      fieldSettings: {
        hideLabel: true,
        hintPlace: 'field',
      },
    };
    checkboxConfig.props.captions$ = of(captions);
    const checkboxValue = DocHelper.getValue(
      checkboxName,
      state.docSubject.value,
    );
    checkboxConfig.defaultValue = checkboxValue || 'off';
    return checkboxConfig;
  });
}

/**
 * Get filed group for filter
 *
 * @param props
 * @param typedControl
 * @param field
 * @param state
 */
function getListFilterFieldGroup(
  props: ListFilterProps,
  typedControl: ListFilterControlWithValue,
  field: IField,
  state: ISPFormState,
): (
  | ISPFieldConfig<ISPFieldType.Checkbox>
  | ISPFieldConfig<ISPFieldType.Select>
  | ISPFieldConfig<ISPFieldType.Switcher>
)[] {
  switch (props.displayType) {
    case 'checkbox':
      return getCheckboxConfigs(
        // @TODO i.ablov make this checkboxes streamable
        getListFilterTags(typedControl.control, state.docSubject.value),
        typedControl.control.$src_field,
        field,
        state,
      );
    case 'multiple':
    case 'select':
      return [getSelectConfigWithOptions(props, typedControl, field, state)];
    case 'switcher':
      return [getSwitcherConfigWithOptions(props, typedControl, field, state)];
  }
}

/**
 * Get props for list filter field
 *
 * @param control
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getListFilterProps(
  control: IListFilterBinder,
  field: IField,
  state: ISPFormState,
): ListFilterProps {
  const props: ListFilterProps = {
    originalBinder: control,
    originalField: field,
    fieldSettings$: of({
      // @TODO i.ablov this offset distinct from default because radiogroup have strange padding, that visually looks like 15px between label and radiogroup
      // remove padding from radiogroup and remove this offset!
      labelOffset: '12px',
    }),
    tags$: getListFilterTags$(control, state),
    listName: control.$src_list,
    targetProperty: control.$src_field,
    displayType: control.$type || 'checkbox',
    resetMsg$: DocHelper.getMessage$('filterclear', state.docSubject),
    style: {
      minWidth: 'min-content',
    },
    layoutPlace: getLayoutPlace(control, field),
  };

  return props;
}

/**
 * Get config for list filter field
 *
 * @param typedControl - list filter typed control
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getListFilterConfig(
  typedControl: ListFilterControlWithValue,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.ListFilter> {
  const props = getListFilterProps(typedControl.control, field, state);

  const fieldGroup = getListFilterFieldGroup(props, typedControl, field, state);

  return {
    type: ISPFieldType.ListFilter,
    wrappers: [ISPFieldWrapper.FieldBase],
    fieldGroup,
    props,
    wrapIntoInlineGroupIfNeeded: true,
  };
}
